/**
 * Finance Dashboard (FIN-13) — the `/finance` landing screen (replaces the
 * FIN-9 redirect to Accounts).
 *
 * Everything here renders SERVER analytics verbatim (design §8): the dashboard
 * never recomputes income/expense figures client-side. It composes the existing
 * FIN-8 analytics hooks + the FIN-11 review-counts hook into a calm overview.
 *
 * A configurable time-range control at the top (This month · Last month · Last
 * 3 months · Last 12 months · Custom) drives the KPI cards, the income-vs-
 * expense trend, and the account-balance-evolution candlestick. The
 * budget-progress and review pieces stay current-month — they're about "right
 * now", not the reporting window.
 *
 *   - range-scoped KPI cards: Income (Σ moneyIn), Expenses (Σ moneyOut),
 *     Profit/Loss (Income − Expenses)
 *   - net-worth card (latest month in the range window)
 *   - income vs expense trend line chart over the range
 *   - account balance evolution candlestick (weekly OHLC) with account selector
 *   - budget progress (top 5 expense categories by % of budget used) — this month
 *   - savings-flow mini chart (over the range)
 *   - outstanding professional-recovery card (hidden until settings configured)
 *   - pending-review banner → /finance/review
 *   - recent transactions (last 10)
 *
 * Tokens + CSS Modules only; all chart theming flows through `charts.tsx`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { BalanceTooltip } from '../../components/finance/BalanceTooltip';
import { Icon } from '@k-os/ui';
import {
  useCashflow,
  useNetWorth,
  useSavingsFlow,
  useBudgetVsActual,
  useProfessionalRecovery,
  useFinanceReviewCounts,
  useFinanceTransactions,
  useFinanceAccounts,
  useFinanceCategories,
  useBalanceEvolution,
  type BudgetVsActualRow,
  type BalanceEvolutionWeekDto,
} from '../../api/finance';
import { formatEur, financeErrorCode, financeErrorMessage } from './finance-format';
import {
  currentMonth,
  monthLabel,
  isValidMonth,
  dashboardPresetRange,
  dashboardRangeWord,
  monthStartIso,
  monthEndIso,
  DASHBOARD_PRESET_LABELS,
  type DashboardRangePreset,
} from './report-format';
import {
  FinanceLineChart,
  FinanceCandlestickChart,
  FinanceBarChart,
  MINI_CHART_HEIGHT,
  shortMonth,
  shortWeek,
  useChartTheme,
  type CandleDatum,
} from '../../components/finance/charts';
import styles from './finance.module.css';
import dash from './Dashboard.module.css';

// ─── Small presentational helpers ─────────────────────────────────────────────

function StatCard({
  label,
  value,
  tone,
  children,
}: {
  label: string;
  value: string;
  tone?: 'pos' | 'neg' | 'net';
  children?: React.ReactNode;
}) {
  const valueClass =
    tone === 'pos' ? dash.valuePos : tone === 'neg' ? dash.valueNeg : dash.valueNet;
  return (
    <div className={dash.card}>
      <div className={dash.cardLabel}>{label}</div>
      <div className={`${dash.cardValue} ${valueClass}`}>{value}</div>
      {children}
    </div>
  );
}

// ─── Time-range control ────────────────────────────────────────────────────────

const PRESET_ORDER: DashboardRangePreset[] = [
  'this-month',
  'last-month',
  'last-3-months',
  'last-12-months',
  'custom',
];

interface MonthRange {
  fromMonth: string;
  toMonth: string;
}

function RangeControl({
  preset,
  range,
  onPreset,
  onCustom,
}: {
  preset: DashboardRangePreset;
  range: MonthRange;
  onPreset: (p: DashboardRangePreset) => void;
  onCustom: (range: MonthRange) => void;
}) {
  return (
    <div className={dash.rangeBar}>
      <div className={dash.rangePresets}>
        {PRESET_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            className={`${dash.rangeBtn} ${preset === p ? dash.rangeBtnActive : ''}`}
            onClick={() => onPreset(p)}
          >
            {DASHBOARD_PRESET_LABELS[p]}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className={dash.rangeCustom}>
          <label className={dash.rangeField}>
            <span className={dash.rangeFieldLabel}>From</span>
            <input
              type="month"
              className={styles.filterControl}
              value={range.fromMonth}
              max={range.toMonth}
              onChange={(e) => onCustom({ ...range, fromMonth: e.target.value })}
            />
          </label>
          <label className={dash.rangeField}>
            <span className={dash.rangeFieldLabel}>To</span>
            <input
              type="month"
              className={styles.filterControl}
              value={range.toMonth}
              min={range.fromMonth}
              onChange={(e) => onCustom({ ...range, toMonth: e.target.value })}
            />
          </label>
        </div>
      )}
    </div>
  );
}

// ─── Range-scoped KPI cards (Income / Expenses / Profit-Loss) ─────────────────

function RangeKpis({
  from,
  to,
  rangeWord,
}: {
  from: string;
  to: string;
  rangeWord: string;
}) {
  const q = useCashflow(from, to);
  const months = useMemo(() => q.data ?? [], [q.data]);

  const { income, expenses } = useMemo(
    () =>
      months.reduce(
        (acc, m) => {
          acc.income += m.moneyIn;
          acc.expenses += m.moneyOut;
          return acc;
        },
        { income: 0, expenses: 0 },
      ),
    [months],
  );
  const profit = income - expenses;

  return (
    <>
      <StatCard label={`${rangeWord} Income`} value={formatEur(income)} tone="pos" />
      <StatCard label={`${rangeWord} Expenses`} value={formatEur(expenses)} tone="neg" />
      <StatCard
        label={`${rangeWord} Profit / Loss`}
        value={formatEur(profit)}
        tone={profit >= 0 ? 'pos' : 'neg'}
      />
    </>
  );
}

// ─── Income vs Expense trend (line chart over the range) ──────────────────────

function IncomeExpenseTrend({ from, to }: { from: string; to: string }) {
  const theme = useChartTheme();
  const q = useCashflow(from, to);
  const months = q.data ?? [];
  const hasData = months.length > 0;

  return (
    <section className={dash.panel}>
      <div className={dash.panelHead}>
        <h3 className={dash.panelTitle}>Income vs expense trend</h3>
        <Link to="/finance/reports" className={styles.linkBtn}>
          Reports →
        </Link>
      </div>
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.muted}>No transactions in this range.</div>
      ) : (
        <FinanceLineChart
          data={months}
          xKey="month"
          lines={[
            { key: 'moneyIn', name: 'Income', color: theme.positive },
            { key: 'moneyOut', name: 'Expenses', color: theme.negative },
          ]}
          xTickFormatter={(v) => shortMonth(v)}
        />
      )}
    </section>
  );
}

// ─── Account balance evolution candlestick (weekly OHLC + account selector) ───

function AccountSelector({
  accounts,
  selected,
  onToggle,
}: {
  accounts: { id: string; name: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const count = selected.size;
  const label =
    count === accounts.length ? 'All accounts' : `${count} of ${accounts.length}`;

  return (
    <div className={dash.acctSelect} ref={ref}>
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Icon name="wallet" size={13} /> {label}
        <Icon name="chevronDown" size={12} />
      </button>
      {open && (
        <div className={dash.acctMenu}>
          <div className={dash.acctMenuHead}>Accounts to display</div>
          {accounts.map((a) => {
            const on = selected.has(a.id);
            return (
              <button
                key={a.id}
                type="button"
                className={dash.acctOption}
                onClick={() => onToggle(a.id)}
              >
                <span className={`${dash.acctCheck} ${on ? dash.acctCheckOn : ''}`}>
                  {on && <Icon name="check" size={11} />}
                </span>
                <span className={dash.acctOptionName}>{a.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BalanceEvolution({ from, to }: { from: string; to: string }) {
  const accountsQ = useFinanceAccounts();
  const accounts = useMemo(
    () => (accountsQ.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accountsQ.data],
  );

  // Default: all accounts selected. Re-sync when the account list first loads
  // (or changes) so newly-added accounts are included by default.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const accountIdsKey = accounts.map((a) => a.id).join(',');
  useEffect(() => {
    setSelected(new Set(accounts.map((a) => a.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountIdsKey]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Pass the selection only when it's a strict subset; all-selected → undefined
  // (server returns every account, matching the "All accounts" label).
  const selectedIds =
    selected.size > 0 && selected.size < accounts.length
      ? [...selected]
      : undefined;

  const q = useBalanceEvolution(from, to, selectedIds);
  const weeks = useMemo(() => q.data ?? [], [q.data]);

  // Index weeks by their x key so the tooltip can recover the full week object
  // (with the per-account breakdown) from the candle datum.
  const weekByKey = useMemo(() => {
    const m = new Map<string, BalanceEvolutionWeekDto>();
    for (const w of weeks) m.set(w.weekStart, w);
    return m;
  }, [weeks]);

  const candles: CandleDatum[] = useMemo(
    () =>
      weeks.map((w) => ({
        x: w.weekStart,
        open: w.open,
        close: w.close,
        high: w.high,
        low: w.low,
      })),
    [weeks],
  );

  const hasData = weeks.length > 0 && selected.size > 0;

  return (
    <section className={dash.panel}>
      <div className={dash.panelHead}>
        <h3 className={dash.panelTitle}>Account balance evolution</h3>
        <AccountSelector accounts={accounts} selected={selected} onToggle={toggle} />
      </div>
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : selected.size === 0 ? (
        <div className={styles.muted}>Select at least one account to display.</div>
      ) : !hasData ? (
        <div className={styles.muted}>No balance history in this range.</div>
      ) : (
        <FinanceCandlestickChart
          data={candles}
          xTickFormatter={(v) => shortWeek(v)}
          tooltip={(datum) => (
            <BalanceTooltip week={datum ? weekByKey.get(datum.x) ?? null : null} />
          )}
        />
      )}
    </section>
  );
}

// ─── Budget progress (top 5 by % used) — current month ────────────────────────

function budgetPct(row: BudgetVsActualRow): number {
  return row.budget > 0 ? (row.actual / row.budget) * 100 : 0;
}

function BudgetProgress({ month }: { month: string }) {
  const bvaQ = useBudgetVsActual(month);
  const rows = useMemo(() => bvaQ.data ?? [], [bvaQ.data]);

  const top = useMemo(
    () =>
      rows
        .filter((r) => r.budget > 0)
        .sort((a, b) => budgetPct(b) - budgetPct(a))
        .slice(0, 5),
    [rows],
  );

  return (
    <section className={dash.panel}>
      <div className={dash.panelHead}>
        <h3 className={dash.panelTitle}>Budget progress</h3>
        <Link to="/finance/budgets" className={styles.linkBtn}>
          Budgets →
        </Link>
      </div>
      {bvaQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : top.length === 0 ? (
        <div className={styles.muted}>No budgets set for {monthLabel(month)}.</div>
      ) : (
        <div className={dash.budgetList}>
          {top.map((r) => {
            const pct = budgetPct(r);
            const capped = Math.min(pct, 100);
            const cls =
              pct >= 95 ? dash.barRed : pct >= 80 ? dash.barAmber : dash.barGreen;
            return (
              <div key={r.categoryId} className={dash.budgetItem}>
                <div className={dash.budgetItemHead}>
                  <span className={dash.budgetName}>{r.name}</span>
                  <span className={`${styles.num} ${dash.budgetFig}`}>
                    {formatEur(r.actual)} / {formatEur(r.budget)}
                  </span>
                </div>
                <div className={dash.barTrack}>
                  <div className={`${dash.barFill} ${cls}`} style={{ width: `${capped}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Savings-flow mini chart (over the range) ─────────────────────────────────

function SavingsFlowMini({ from, to }: { from: string; to: string }) {
  const theme = useChartTheme();
  const q = useSavingsFlow(from, to);
  const data = q.data ?? [];
  const hasData = data.some((d) => d.inflow !== 0 || d.outflow !== 0);

  return (
    <section className={dash.panel}>
      <div className={dash.panelHead}>
        <h3 className={dash.panelTitle}>Savings flow</h3>
        <Link to="/finance/reports" className={styles.linkBtn}>
          Reports →
        </Link>
      </div>
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.muted}>No savings transfers in this range.</div>
      ) : (
        <FinanceBarChart
          data={data}
          xKey="month"
          bars={[{ key: 'net', name: 'Net into savings', color: theme.net }]}
          height={MINI_CHART_HEIGHT}
          xTickFormatter={(v) => shortMonth(v)}
        />
      )}
    </section>
  );
}

// ─── Professional recovery card (hidden until settings configured) ────────────

function ProfessionalRecoveryCard({ from, to }: { from: string; to: string }) {
  const q = useProfessionalRecovery(from, to);
  if (q.isError && financeErrorCode(q.error) === 'settings_required') return null;
  if (q.isLoading || !q.data) return null;

  const outstanding = q.data.outstanding;
  return (
    <StatCard
      label="Outstanding to recover"
      value={formatEur(outstanding)}
      tone={outstanding > 0 ? 'neg' : 'pos'}
    >
      <Link to="/finance/reports" className={styles.linkBtn}>
        Professional recovery →
      </Link>
    </StatCard>
  );
}

// ─── Recent transactions (last 10) ────────────────────────────────────────────

function RecentTransactions() {
  const txQ = useFinanceTransactions({ limit: 10, transferLegs: 'exclude' });
  const accountsQ = useFinanceAccounts({ includeArchived: true });
  const categoriesQ = useFinanceCategories({ includeInactive: true });

  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accountsQ.data ?? []) m.set(a.id, a.name);
    return m;
  }, [accountsQ.data]);

  const categoryName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categoriesQ.data ?? []) m.set(c.id, c.name);
    return m;
  }, [categoriesQ.data]);

  const transactions = txQ.data?.transactions ?? [];

  return (
    <section className={dash.panel}>
      <div className={dash.panelHead}>
        <h3 className={dash.panelTitle}>Recent activity</h3>
        <Link to="/finance/transactions" className={styles.linkBtn}>
          All transactions →
        </Link>
      </div>
      {txQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : txQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(txQ.error)}</div>
      ) : transactions.length === 0 ? (
        <div className={styles.muted}>No transactions yet. Import a statement to begin.</div>
      ) : (
        <div className={dash.recentList}>
          {transactions.map((t) => (
            <div key={t.id} className={dash.recentRow}>
              <div className={dash.recentMain}>
                <span className={dash.recentDesc}>{t.description}</span>
                <span className={dash.recentMeta}>
                  {t.date} · {accountName.get(t.accountId) ?? '—'}
                  {t.categoryId && categoryName.has(t.categoryId)
                    ? ` · ${categoryName.get(t.categoryId)}`
                    : ''}
                </span>
              </div>
              <span
                className={`${styles.num} ${t.amountEur < 0 ? styles.amtNeg : styles.amtPos}`}
              >
                {formatEur(t.amountEur)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Pending-review banner ────────────────────────────────────────────────────

function ReviewBanner() {
  const q = useFinanceReviewCounts();
  const c = q.data;
  if (!c) return null;
  const total = c.suggestedTransfers + c.pendingSuggestions + c.uncategorized;
  if (total === 0) return null;

  const parts: string[] = [];
  if (c.uncategorized > 0) parts.push(`${c.uncategorized} uncategorized`);
  if (c.suggestedTransfers > 0) parts.push(`${c.suggestedTransfers} suggested transfer${c.suggestedTransfers !== 1 ? 's' : ''}`);
  if (c.pendingSuggestions > 0) parts.push(`${c.pendingSuggestions} agent suggestion${c.pendingSuggestions !== 1 ? 's' : ''}`);

  return (
    <Link to="/finance/review" className={styles.reviewBanner}>
      <span>
        <strong>{total}</strong> item{total !== 1 ? 's' : ''} to review — {parts.join(', ')}.
      </span>
      <span aria-hidden>→</span>
    </Link>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export function FinanceDashboardScreen() {
  const month = currentMonth();

  // Time range driving the KPIs, trend and balance-evolution candlestick.
  // Default: Last 12 months.
  const [preset, setPreset] = useState<DashboardRangePreset>('last-12-months');
  const [range, setRange] = useState<MonthRange>(() =>
    dashboardPresetRange('last-12-months'),
  );

  const handlePreset = (p: DashboardRangePreset) => {
    setPreset(p);
    if (p !== 'custom') setRange(dashboardPresetRange(p));
    // 'custom' keeps the current range as the starting point for editing.
  };

  const validRange =
    isValidMonth(range.fromMonth) &&
    isValidMonth(range.toMonth) &&
    range.fromMonth <= range.toMonth;

  const { fromMonth, toMonth } = range;
  const rangeWord = dashboardRangeWord(preset);

  // Net worth over the selected window; the latest month is the headline figure.
  const netWorthQ = useNetWorth(fromMonth, toMonth);
  const netWorthMonths = netWorthQ.data ?? [];
  const latestNetWorth =
    netWorthMonths.length > 0 ? netWorthMonths[netWorthMonths.length - 1] : undefined;

  // ISO bounds for the balance-evolution endpoint (first day of from, last of to).
  const fromIso = monthStartIso(fromMonth);
  const toIso = monthEndIso(toMonth);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Dashboard</h2>
          <div className={styles.subtitle}>
            {monthLabel(fromMonth)}
            {fromMonth !== toMonth ? ` – ${monthLabel(toMonth)}` : ''} overview.
          </div>
        </div>
        <div className={styles.actions}>
          <Link to="/finance/import" className="kos-btn kos-btn-primary">
            Import statement
          </Link>
        </div>
      </header>

      <RangeControl
        preset={preset}
        range={range}
        onPreset={handlePreset}
        onCustom={setRange}
      />

      <ReviewBanner />

      {!validRange ? (
        <div className={styles.empty}>
          Pick a valid month range (From must be on or before To).
        </div>
      ) : (
        <>
          {/* Headline cards — range-scoped income / expenses / P&L + net worth */}
          <div className={dash.cardGrid}>
            <RangeKpis from={fromMonth} to={toMonth} rangeWord={rangeWord} />
            <StatCard
              label="Net worth"
              value={latestNetWorth ? formatEur(latestNetWorth.net) : '—'}
              tone="net"
            >
              {latestNetWorth && (
                <span className={dash.cardSub}>
                  {formatEur(latestNetWorth.assets)} assets ·{' '}
                  {formatEur(latestNetWorth.liabilities)} liabilities
                </span>
              )}
            </StatCard>
            <ProfessionalRecoveryCard from={fromMonth} to={toMonth} />
          </div>

          {/* Trend + candlestick */}
          <div className={dash.bodyGrid}>
            <IncomeExpenseTrend from={fromMonth} to={toMonth} />
            <BalanceEvolution from={fromIso} to={toIso} />
          </div>

          {/* Budget (current month) + savings flow (range) */}
          <div className={dash.bodyGrid}>
            <BudgetProgress month={month} />
            <SavingsFlowMini from={fromMonth} to={toMonth} />
          </div>

          <RecentTransactions />
        </>
      )}
    </div>
  );
}
